import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';
import ToastUtils from '@/utils/toast-utils';
import { tabStateManager } from '@/utils/tabStateManager';

export interface RequestConfig extends AxiosRequestConfig {
  showLoading?: boolean;
  showError?: boolean;
}

class HttpRequest {
  private instance: AxiosInstance;
  private baseConfig: RequestConfig = {
    timeout: 10000,
    showLoading: true,
    showError: true,
  };

  constructor(config: RequestConfig = {}) {
    this.instance = axios.create({
      ...this.baseConfig,
      ...config,
    });
    this.setupInterceptors();
  }

  // 设置拦截器
  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const requestConfig = config as InternalAxiosRequestConfig & RequestConfig;

        // 显示loading
        if (requestConfig.showLoading) {
          this.showLoading();
        }

        // 获取标签页独立的token并添加到请求头
        const accessToken = tabStateManager.getAccessToken();
        const tenantId = tabStateManager.getTenantId();
        // 内部token通常是全局的，保持原有逻辑
        const internalToken = localStorage.getItem('internal_token');

        if (accessToken && requestConfig.headers) {
          requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // 添加租户ID头部（App APIs需要）
        if (tenantId && requestConfig.headers) {
          requestConfig.headers['X-Tenant-ID'] = tenantId;
        }

        // 添加内部认证token（Internal & Tenant APIs需要）
        if (internalToken && requestConfig.headers) {
          requestConfig.headers['x-internal-token'] = internalToken;
        }

        return requestConfig;
      },
      (error) => {
        this.hideLoading();
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 隐藏loading
        const config = response.config as RequestConfig;
        if (config.showLoading) {
          this.hideLoading();
        }

        // 处理响应数据
        const { data } = response;
        if (data.code === 0 || data.code === 200) {
          // 确保返回的数据包含code信息
          const result = data.data || {};
          result.code = data.code;
          return result;
        } else {
          if (config.showError) {
            this.showError(data.err_msg || 'request failed');
          }
          return Promise.reject(data);
        }
      },
      (error) => {
        // 隐藏loading
        const config = error.config as RequestConfig;
        if (config?.showLoading) {
          this.hideLoading();
        }

        // 处理错误信息
        if (config?.showError) {
          const errorMsg = this.getErrorMessage(error);
          this.showError(errorMsg);
        }

        // 处理特定状态码
        if (error.response) {
          switch (error.response.status) {
            case 401:
              // 未授权处理，例如跳转到登录页
              // 清除当前标签页的认证相关存储
              tabStateManager.clearAuth();
              // 内部token是全局的，保持原有清理逻辑
              localStorage.removeItem('internal_token');
              window.location.href = '/auth/organization';
              break;
            case 403:
              // 权限不足处理
              break;
            case 404:
              // 资源不存在处理
              break;
            case 500:
              // 服务器错误处理
              break;
            default:
              break;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // 显示加载状态
  private showLoading() {
    // 这里可以实现显示loading的逻辑
    // 例如使用全局状态管理或UI组件库
    console.log('Loading...');
  }

  // 隐藏加载状态
  private hideLoading() {
    // 这里可以实现隐藏loading的逻辑
    console.log('Loading complete');
  }

  // 显示错误信息
  private showError(message: string) {
    // 使用 toast 显示错误信息
    ToastUtils.showToastFail(message);
    console.error("showError === ", message);
  }

  // 获取错误信息
  private getErrorMessage(error: any): string {
    if (error.response?.data) {
      const data = error.response.data as ApiResponse;
      return data.err_msg || 'request failed';
    }
    return error.message || 'network error';
  }

  // 请求方法封装
  private request<T = any>(config: RequestConfig): Promise<T> {
    return this.instance.request(config);
  }

  // GET请求
  public get<T = any>(url: string, params?: any, config: RequestConfig = {}): Promise<T> {
    return this.request<T>({
      method: 'GET',
      url,
      params,
      ...config,
    });
  }

  // POST请求
  public post<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.request<T>({
      method: 'POST',
      url,
      data,
      ...config,
    });
  }

  // PUT请求
  public put<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.request<T>({
      method: 'PUT',
      url,
      data,
      ...config,
    });
  }

  // DELETE请求
  public delete<T = any>(url: string, params?: any, config: RequestConfig = {}): Promise<T> {
    return this.request<T>({
      method: 'DELETE',
      url,
      params,
      ...config,
    });
  }

  // PATCH请求
  public patch<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.request<T>({
      method: 'PATCH',
      url,
      data,
      ...config,
    });
  }
}

// 导出请求实例
export const http = new HttpRequest();
export default HttpRequest;
